import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MaxLanguagesCount, requestDefaultLanguage } from '@constants';
import {
  RequestItem,
  RequestLanguage,
  useControlFormField,
  useOptionsApi,
  useValidationSchemaRequest,
} from '@innowise-group/core';
import { Icon, Input, Select } from '@innowise-group/mui-kit';

import { FormSection } from '../form-section';
import * as Styled from './languages-section.styles';

const LanguagesSection: React.FC = () => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<RequestItem>();
  const { languageOptions, languageLevelOptions } = useOptionsApi();
  const { validationSchema } = useValidationSchemaRequest();
  const { handleAddItem, handleRemoveItem, handleChangeSelectItem } = useControlFormField(validationSchema);
  const languages = watch('languages') || [];

  const canRemoveLanguage = languages.length > 1;
  const canAddLanguage = languages.length < MaxLanguagesCount;

  return (
    <FormSection title={t('pages.requests.creationModal.candidateLanguages')}>
      {languages.map((_, index) => {
        const selectedInOtherRows = new Set(
          languages.filter((curr, idx) => idx !== index && !!curr.language).map((curr) => curr.language),
        );
        const availableLanguageOptions = (languageOptions || []).filter(
          (option) => !selectedInOtherRows.has(option.value),
        );
        const labelSuffix = index ? ` ${index + 1}` : '';

        return (
          <React.Fragment key={index}>
            <Controller
              name={`languages.${index}.language`}
              control={control}
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <Select
                  value={value}
                  onChange={handleChangeSelectItem(onChange, 'languages')}
                  options={availableLanguageOptions}
                  disableSortByAlphabet={false}
                  renderInput={(params) => (
                    <Input
                      {...params}
                      label={`${t('pages.vacancies.creationModal.language')}${labelSuffix}`}
                      placeholder={t('pages.vacancies.creationModal.selectLanguage')}
                      error={!!(errors?.languages?.[index]?.language?.message || errors?.languages?.[index]?.message)}
                      helperText={(
                        errors?.languages?.[index]?.language?.message || errors?.languages?.[index]?.message
                      )?.toString()}
                    />
                  )}
                />
              )}
            />
            <Controller
              name={`languages.${index}.level`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  value={value}
                  onChange={handleChangeSelectItem(onChange, 'languages')}
                  options={languageLevelOptions || []}
                  renderInput={(params) => (
                    <Input
                      {...params}
                      label={`${t('pages.vacancies.creationModal.level')}${labelSuffix}`}
                      placeholder={t('pages.vacancies.creationModal.selectLevel')}
                    />
                  )}
                />
              )}
            />
            <Styled.RowTools>
              {canRemoveLanguage ? (
                <Styled.RemoveButton
                  aria-label={t('buttons.delete')}
                  onClick={handleRemoveItem<RequestLanguage>(index, 'languages', languages)}
                >
                  <Icon type="u_times" size={24} />
                </Styled.RemoveButton>
              ) : null}
              {!index && canAddLanguage ? (
                <Styled.AddButton
                  variant="outlined"
                  onClick={handleAddItem<RequestLanguage>('languages', languages, { ...requestDefaultLanguage })}
                  startIcon={<Icon type="u_plus" size={14} />}
                >
                  {t('buttons.more')}
                </Styled.AddButton>
              ) : null}
            </Styled.RowTools>
          </React.Fragment>
        );
      })}
    </FormSection>
  );
};

export default LanguagesSection;
